import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import VendorCard from '../components/VendorCard';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Heart,
  ListTodo,
  DollarSign,
  Mail,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [budget, setBudget] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [favRes, checkRes, budgetRes, invRes] = await Promise.all([
        axios.get(`${API}/favorites`, { withCredentials: true }),
        axios.get(`${API}/checklist`, { withCredentials: true }),
        axios.get(`${API}/budget`, { withCredentials: true }),
        axios.get(`${API}/invitations`, { withCredentials: true }),
      ]);
      setFavorites(favRes.data || []);
      setChecklist(checkRes.data || []);
      setBudget(budgetRes.data || []);
      setInvitations(invRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const completedTasks = checklist.filter(t => t.is_completed).length;
  const totalTasks = checklist.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const totalBudget = budget.reduce((sum, item) => sum + (item.estimated_cost || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
              )}
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-gray-900">
                  Welcome, {user?.name?.split(' ')[0]}
                </h1>
                <p className="text-gray-500">Let's plan your dream wedding</p>
              </div>
            </div>
            <Link to="/vendors">
              <Button className="btn-primary">
                <Sparkles className="w-4 h-4 mr-2" />
                Find Vendors
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                <Heart className="w-5 h-5 text-teal-600" />
              </div>
              <span className="text-2xl font-bold">{favorites.length}</span>
            </div>
            <p className="text-sm text-gray-500">Saved Vendors</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <ListTodo className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-2xl font-bold">{completedTasks}/{totalTasks}</span>
            </div>
            <p className="text-sm text-gray-500">Tasks Done</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-2xl font-bold">${(totalBudget/1000).toFixed(0)}k</span>
            </div>
            <p className="text-sm text-gray-500">Budget</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-2xl font-bold">{invitations.length}</span>
            </div>
            <p className="text-sm text-gray-500">Invitations</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Planning Progress */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg">Planning Progress</h2>
                <Link to="/planning" className="text-sm text-teal-600 hover:underline flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-gray-500">{completedTasks} of {totalTasks} tasks completed</span>
                <span className="font-medium">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              
              {totalTasks === 0 && (
                <div className="mt-4 text-center py-6 bg-gray-50 rounded-xl">
                  <p className="text-gray-500 mb-3">Get started with our checklist</p>
                  <Link to="/planning">
                    <Button size="sm" className="rounded-lg">Create Checklist</Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Saved Vendors */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg">Saved Vendors</h2>
                <Link to="/vendors" className="text-sm text-teal-600 hover:underline flex items-center gap-1">
                  Browse <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {favorites.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Heart className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 mb-3">No saved vendors yet</p>
                  <Link to="/vendors">
                    <Button size="sm" className="rounded-lg">Find Vendors</Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {favorites.slice(0, 4).map((vendor) => (
                    <VendorCard key={vendor.id} vendor={vendor} isFavorited />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="font-heading font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link to="/planning" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                    <ListTodo className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="font-medium text-gray-700">Checklist</span>
                </Link>
                <Link to="/planning" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="font-medium text-gray-700">Budget Tracker</span>
                </Link>
                <Link to="/invitations" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="font-medium text-gray-700">E-Invitations</span>
                </Link>
                <Link to="/inspiration" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                  </div>
                  <span className="font-medium text-gray-700">Inspiration</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
